import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SignOut, Plus, MagnifyingGlass, Spinner, Warning, CheckCircle, Clock, XCircle } from '@phosphor-icons/react';
import { dashboardApi, domainsApi, registrarsApi, dnsProvidersApi, ambientesApi, empresasApi } from '../services/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import DomainModal from '../components/DomainModal';
import DomainDetailSheet from '../components/DomainDetailSheet';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [domains, setDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [registrars, setRegistrars] = useState([]);
  const [dnsProviders, setDnsProviders] = useState([]);
  const [ambientes, setAmbientes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('vencimiento');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [domains, searchTerm, filterStatus, sortBy, sortOrder]);

  const loadData = async () => {
    try {
      const [statsRes, domainsRes, registrarsRes, dnsRes, ambientesRes, empresasRes] = await Promise.all([
        dashboardApi.getStats(),
        domainsApi.getAll(),
        registrarsApi.getAll(),
        dnsProvidersApi.getAll(),
        ambientesApi.getAll(),
        empresasApi.getAll(),
      ]);
      
      setStats(statsRes.data);
      setDomains(domainsRes.data);
      setRegistrars(registrarsRes.data);
      setDnsProviders(dnsRes.data);
      setAmbientes(ambientesRes.data);
      setEmpresas(empresasRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...domains];

    if (searchTerm) {
      filtered = filtered.filter(d => 
        (d.dominio || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.empresa_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.ambiente_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.registrador_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.dns_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(d => d.estado_vencimiento === filterStatus);
    }

    // Ordenar
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortBy === 'vencimiento') {
        aVal = a.vencimiento ? new Date(a.vencimiento).getTime() : 0;
        bVal = b.vencimiento ? new Date(b.vencimiento).getTime() : 0;
      } else if (sortBy === 'dias_para_vencer') {
        aVal = a.dias_para_vencer || 999;
        bVal = b.dias_para_vencer || 999;
      } else if (sortBy === 'ambiente_name' || sortBy === 'empresa_name') {
        aVal = String(a[sortBy] || '').toLowerCase();
        bVal = String(b[sortBy] || '').toLowerCase();
      } else {
        aVal = String(a[sortBy] || '').toLowerCase();
        bVal = String(b[sortBy] || '').toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredDomains(filtered);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleAddDomain = () => {
    setEditingDomain(null);
    setShowModal(true);
  };

  const handleEditDomain = (domain) => {
    setEditingDomain(domain);
    setShowModal(true);
  };

  const handleDeleteDomain = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este dominio?')) return;
    
    try {
      await domainsApi.delete(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting domain:', error);
      alert('Error al eliminar el dominio');
    }
  };

  const handleViewDetails = (domain) => {
    setSelectedDomain(domain);
  };

  const getStatusBadge = (estado) => {
    switch (estado) {
      case 'vencido':
        return <span className="badge-danger"><XCircle size={14} className="mr-1" />Vencido</span>;
      case 'critico':
        return <span className="badge-danger"><Warning size={14} className="mr-1" />Crítico</span>;
      case 'proximo':
        return <span className="badge-warning"><Clock size={14} className="mr-1" />Próximo</span>;
      default:
        return <span className="badge-success"><CheckCircle size={14} className="mr-1" />Normal</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="animate-spin text-[#002FA7]" size={48} />
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-white">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif', color: '#002FA7' }}>
            Domain Manager
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-zinc-600 font-medium">{user?.email}</span>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900 font-semibold"
            >
              <SignOut size={20} />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
          <div 
            data-testid="stat-total-domains" 
            className="stat-card cursor-pointer"
            onClick={() => setFilterStatus('all')}
            style={{ borderLeft: filterStatus === 'all' ? '4px solid #002FA7' : 'none' }}
          >
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Total Dominios</p>
            <p className="text-4xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{stats?.total_domains || 0}</p>
          </div>

          <div 
            data-testid="stat-expiring-60-days" 
            className="stat-card border-l-4 cursor-pointer"
            onClick={() => {
              // Alterna entre mostrar todos los que vencen en 60 días o volver a "all"
              if (filterStatus === 'critico' || filterStatus === 'proximo') {
                setFilterStatus('all');
              } else {
                setFilterStatus('proximo'); // Mostrar próximos por defecto
              }
            }}
            style={{ 
              borderLeftColor: '#FF3B30',
              opacity: (filterStatus === 'critico' || filterStatus === 'proximo') ? 1 : 0.7 
            }}
          >
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Vencen &lt; 60 días</p>
            <p className="text-4xl font-black text-[#FF3B30]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {stats?.domains_expiring_60_days || 0}
            </p>
          </div>

          <div 
            data-testid="stat-critical" 
            className="stat-card cursor-pointer"
            onClick={() => setFilterStatus(filterStatus === 'critico' ? 'all' : 'critico')}
            style={{ borderLeft: filterStatus === 'critico' ? '4px solid #FF3B30' : 'none' }}
          >
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Críticos (0-30d)</p>
            <p className="text-4xl font-black text-[#FF3B30]" style={{ fontFamily: 'Chivo, sans-serif' }}>{stats?.criticos || 0}</p>
          </div>

          <div 
            data-testid="stat-upcoming" 
            className="stat-card cursor-pointer"
            onClick={() => setFilterStatus(filterStatus === 'proximo' ? 'all' : 'proximo')}
            style={{ borderLeft: filterStatus === 'proximo' ? '4px solid #FFCC00' : 'none' }}
          >
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Próximos (31-60d)</p>
            <p className="text-4xl font-black text-[#FFCC00]" style={{ fontFamily: 'Chivo, sans-serif', color: '#996600' }}>
              {stats?.proximos || 0}
            </p>
          </div>

          <div 
            data-testid="stat-normal" 
            className="stat-card cursor-pointer"
            onClick={() => setFilterStatus(filterStatus === 'normal' ? 'all' : 'normal')}
            style={{ borderLeft: filterStatus === 'normal' ? '4px solid #00C759' : 'none' }}
          >
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Normales</p>
            <p className="text-4xl font-black text-[#00C759]" style={{ fontFamily: 'Chivo, sans-serif' }}>{stats?.normales || 0}</p>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 flex-1 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
              <input
                data-testid="search-domains-input"
                type="text"
                placeholder="Buscar dominios..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <select
              data-testid="filter-status-select"
              className="input max-w-xs"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">Todos los estados</option>
              <option value="vencido">Vencidos</option>
              <option value="critico">Críticos (0-30 días)</option>
              <option value="proximo">Próximos (31-60 días)</option>
              <option value="normal">Normales</option>
              <option value="pendiente">Pendientes verificación</option>
            </select>

            <select
              data-testid="sort-by-select"
              className="input max-w-xs"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="vencimiento">Ordenar por Vencimiento</option>
              <option value="dominio">Ordenar por Dominio</option>
              <option value="ambiente_name">Ordenar por Ambiente</option>
              <option value="empresa_name">Ordenar por Empresa</option>
              <option value="registrador_name">Ordenar por Registrador</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-4 py-2.5 border border-zinc-300 rounded font-semibold text-zinc-700 hover:bg-zinc-50"
            >
              {sortOrder === 'asc' ? '↑ Ascendente' : '↓ Descendente'}
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={async () => {
                const token = localStorage.getItem('access_token');
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/domains/export_pdf/`, {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `dominios_${new Date().toISOString().split('T')[0]}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  alert('Error al exportar PDF');
                }
              }}
              className="px-4 py-2.5 border border-zinc-300 rounded font-semibold text-zinc-700 hover:bg-zinc-50"
            >
              📄 PDF
            </button>
            <button
              onClick={async () => {
                const token = localStorage.getItem('access_token');
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/domains/export_excel/`, {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `dominios_${new Date().toISOString().split('T')[0]}.xlsx`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  alert('Error al exportar Excel');
                }
              }}
              className="px-4 py-2.5 border border-zinc-300 rounded font-semibold text-zinc-700 hover:bg-zinc-50"
            >
              📊 Excel
            </button>
            <button
              data-testid="add-domain-button"
              onClick={handleAddDomain}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={20} weight="bold" />
              Agregar Dominio
            </button>
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Dominio</th>
                <th>Ambiente</th>
                <th>Empresa</th>
                <th>Registrador</th>
                <th>DNS</th>
                <th>Vencimiento</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredDomains.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12">
                    <p className="text-zinc-500 text-lg mb-2">No hay dominios para mostrar</p>
                    <button onClick={handleAddDomain} className="text-[#002FA7] font-semibold hover:underline">
                      Agrega tu primer dominio
                    </button>
                  </td>
                </tr>
              ) : (
                filteredDomains.map((domain) => (
                  <tr key={domain.id} data-testid={`domain-row-${domain.id}`}>
                    <td className="font-semibold text-[#002FA7]">{domain.dominio}</td>
                    <td>{domain.ambiente_name}</td>
                    <td>{domain.empresa_name}</td>
                    <td>{domain.registrador_name}</td>
                    <td>{domain.dns_name}</td>
                    <td>
                      {domain.vencimiento ? format(new Date(domain.vencimiento), 'dd MMM yyyy', { locale: es }) : 'Pendiente'}
                      <span className="text-xs text-zinc-500 ml-2">
                        ({domain.dias_para_vencer !== 999 ? `${domain.dias_para_vencer} días` : 'N/A'})
                      </span>
                    </td>
                    <td>{getStatusBadge(domain.estado_vencimiento)}</td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          data-testid={`view-domain-${domain.id}`}
                          onClick={() => handleViewDetails(domain)}
                          className="text-sm text-[#002FA7] font-semibold hover:underline"
                        >
                          Ver
                        </button>
                        <button
                          data-testid={`edit-domain-${domain.id}`}
                          onClick={() => handleEditDomain(domain)}
                          className="text-sm text-zinc-700 font-semibold hover:underline"
                        >
                          Editar
                        </button>
                        <button
                          data-testid={`delete-domain-${domain.id}`}
                          onClick={() => handleDeleteDomain(domain.id)}
                          className="text-sm text-[#FF3B30] font-semibold hover:underline"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {showModal && (
        <DomainModal
          domain={editingDomain}
          registrars={registrars}
          dnsProviders={dnsProviders}
          ambientes={ambientes}
          empresas={empresas}
          onClose={() => setShowModal(false)}
          onSave={loadData}
        />
      )}

      {selectedDomain && (
        <DomainDetailSheet
          domain={selectedDomain}
          onClose={() => setSelectedDomain(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
